import React, { useState, useEffect, useRef } from 'react';
import { Send, Mic, Image, Smile, MoreHorizontal, Phone, Video, Heart, Clock, Check, CheckCheck, X } from 'lucide-react';
import EnhancedButton from './EnhancedButton';
import { TypingIndicator } from './LoadingStates';

// Enhanced Chat Interface
export const EnhancedChatInterface = ({ match, messages = [], onSendMessage, onVoiceMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const defaultMatch = {
    id: 1,
    name: "Sarah Chen",
    photo: "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85",
    isOnline: true,
    lastSeen: "Active now"
  };

  const defaultMessages = [
    {
      id: 1,
      senderId: match?.id || 2,
      text: "Hey! Thanks for the match! I saw you're into hiking too 🏔️",
      timestamp: new Date(Date.now() - 3600000),
      status: 'read',
      type: 'text'
    },
    {
      id: 2,
      senderId: 'me',
      text: "Hi Sarah! Yes, I love hiking! Have you done any good trails recently?",
      timestamp: new Date(Date.now() - 3300000),
      status: 'read',
      type: 'text'
    },
    {
      id: 3,
      senderId: match?.id || 2,
      text: "Just did Mount Wilson last weekend! The views were incredible. Would love to find a hiking buddy 😊",
      timestamp: new Date(Date.now() - 3000000),
      status: 'read',
      type: 'text'
    },
    {
      id: 4,
      senderId: 'me',
      text: "That sounds amazing! I've been wanting to try that trail. Maybe we could plan something?",
      timestamp: new Date(Date.now() - 2700000),
      status: 'delivered',
      type: 'text'
    }
  ];

  const currentMatch = match || defaultMatch;
  const chatMessages = messages.length > 0 ? messages : defaultMessages;

  const emojis = ['😊', '😂', '❤️', '👍', '🔥', '😍', '🤔', '👋', '🎉', '🌟', '☕', '🏔️'];

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    // Simulate typing indicator
    if (newMessage.length > 0) {
      setIsTyping(true);
      const timer = setTimeout(() => setIsTyping(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [newMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      const newMsg = {
        id: Date.now(),
        senderId: 'me',
        text: newMessage,
        timestamp: new Date(),
        status: 'sending',
        type: 'text'
      };
      
      onSendMessage && onSendMessage(newMsg);
      setNewMessage('');
      
      // Simulate message status updates
      setTimeout(() => {
        // Update to sent
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojis(false);
  };

  const handleVoiceRecord = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // Start recording
      console.log('Starting voice recording...');
    } else {
      // Stop recording and send
      console.log('Stopping voice recording...');
      onVoiceMessage && onVoiceMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageStatus = (status) => {
    switch (status) {
      case 'sending':
        return <Clock className="w-4 h-4 text-gray-400 animate-spin" />;
      case 'sent':
        return <Check className="w-4 h-4 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="w-4 h-4 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-lg overflow-hidden animate-fadeInUp">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-500 text-white p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <img
              src={currentMatch.photo}
              alt={currentMatch.name}
              className="w-12 h-12 rounded-full border-2 border-white"
            />
            {currentMatch.isOnline && (
              <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-lg">{currentMatch.name}</h3>
            <p className="text-rose-100 text-sm">{currentMatch.lastSeen}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <EnhancedButton
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
            icon={<Phone className="w-5 h-5" />}
          />
          <EnhancedButton
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
            icon={<Video className="w-5 h-5" />}
          />
          <EnhancedButton
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
            icon={<MoreHorizontal className="w-5 h-5" />}
          />
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {chatMessages.map((message, index) => {
          const isOwnMessage = message.senderId === 'me';
          const showTimestamp = index === 0 || 
            new Date(message.timestamp).getTime() - new Date(chatMessages[index - 1].timestamp).getTime() > 300000;
          
          return (
            <div key={message.id} className="animate-slideInUp" style={{ animationDelay: `${index * 0.1}s` }}>
              {showTimestamp && (
                <div className="text-center text-xs text-gray-500 mb-2">
                  {new Date(message.timestamp).toLocaleDateString()} {formatTime(message.timestamp)}
                </div>
              )}
              
              <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                <div className="flex items-end space-x-2 max-w-xs lg:max-w-md">
                  {!isOwnMessage && (
                    <img
                      src={currentMatch.photo}
                      alt={currentMatch.name}
                      className="w-8 h-8 rounded-full mb-1"
                    />
                  )}
                  
                  <div className={`px-4 py-2 rounded-2xl transition-all duration-300 hover:scale-105 ${
                    isOwnMessage
                      ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-sm border border-gray-200'
                  }`}>
                    {message.type === 'text' && (
                      <p className="text-sm">{message.text}</p>
                    )}
                    
                    {message.type === 'voice' && (
                      <div className="flex items-center space-x-2">
                        <button className="p-2 bg-white/20 rounded-full">
                          <div className="w-0 h-0 border-l-[6px] border-l-white border-y-[4px] border-y-transparent"></div>
                        </button>
                        <div className="flex-1 h-1 bg-white/30 rounded-full">
                          <div className="h-1 bg-white rounded-full w-1/3"></div>
                        </div>
                        <span className="text-xs opacity-75">0:15</span>
                      </div>
                    )}
                    
                    <div className={`flex items-center justify-end space-x-1 mt-1 ${
                      isOwnMessage ? 'text-white/70' : 'text-gray-400'
                    }`}>
                      <span className="text-xs">{formatTime(message.timestamp)}</span>
                      {isOwnMessage && getMessageStatus(message.status)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="flex items-end space-x-2">
              <img
                src={currentMatch.photo}
                alt={currentMatch.name}
                className="w-8 h-8 rounded-full"
              />
              <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-200">
                <TypingIndicator name={currentMatch.name} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-200 p-4 bg-white">
        <div className="flex items-end space-x-2">
          <div className="flex-1 relative">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="w-full p-3 pr-12 border border-gray-300 rounded-2xl resize-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 max-h-32"
              rows={1}
            />
            
            <div className="absolute right-2 bottom-2 flex items-center space-x-1">
              <button
                onClick={() => setShowEmojis(!showEmojis)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  // Handle image upload
                  console.log('Image selected:', e.target.files[0]);
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Image className="w-5 h-5" />
              </button>
            </div>
            
            {/* Emoji Picker */}
            {showEmojis && (
              <div className="absolute bottom-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 grid grid-cols-6 gap-1 animate-scaleIn">
                {emojis.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleEmojiClick(emoji)}
                    className="p-1 hover:bg-gray-100 rounded text-lg transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <EnhancedButton
            onClick={handleVoiceRecord}
            variant={isRecording ? "success" : "outline"}
            size="sm"
            className={`${isRecording ? 'animate-pulse' : ''}`}
            icon={<Mic className="w-5 h-5" />}
          />
          
          <EnhancedButton
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            icon={<Send className="w-5 h-5" />}
          >
            Send
          </EnhancedButton>
        </div>
      </div>
    </div>
  );
};

// Match Preview Cards for Chat List
export const MatchPreviewCard = ({ match, onClick, lastMessage, unreadCount = 0, isSelected = false }) => {
  const formatLastSeen = (timestamp) => {
    // If it's already a formatted string, return it directly
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diffInHours = (now - lastSeen) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Active now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-center space-x-3 p-4 cursor-pointer transition-colors border-b border-gray-100 animate-slideInLeft ${
        isSelected 
          ? 'bg-rose-50 border-l-4 border-l-rose-500' 
          : 'hover:bg-gray-50'
      }`}
    >
      <div className="relative">
        <img
          src={match.photo}
          alt={match.name}
          className="w-14 h-14 rounded-full object-cover"
        />
        {match.isOnline && (
          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-800 truncate">{match.name}</h4>
          <span className="text-xs text-gray-500">{formatLastSeen(match.lastSeen)}</span>
        </div>
        
        <p className="text-sm text-gray-600 truncate mt-1">
          {lastMessage?.text || "Start a conversation..."}
        </p>
        
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-gray-500">
            {match.compatibility}% match
          </div>
          
          {unreadCount > 0 && (
            <div className="bg-rose-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center animate-bounce">
              {unreadCount}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Call Preview
export const VideoCallPreview = ({ match, onStartCall, onDecline }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 text-center animate-scaleIn">
      <div className="relative mb-6">
        <img
          src={match.photo}
          alt={match.name}
          className="w-24 h-24 rounded-full mx-auto mb-4 animate-pulse"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full"></div>
      </div>
      
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{match.name} wants to video chat</h3>
      <p className="text-gray-600 mb-6">Ready for a face-to-face conversation?</p>
      
      <div className="flex space-x-4 justify-center">
        <EnhancedButton
          onClick={onDecline}
          variant="outline"
          icon={<X className="w-5 h-5" />}
        >
          Decline
        </EnhancedButton>
        <EnhancedButton
          onClick={onStartCall}
          icon={<Video className="w-5 h-5" />}
          className="animate-bounce"
        >
          Accept Call
        </EnhancedButton>
      </div>
    </div>
  );
};

export default {
  EnhancedChatInterface,
  MatchPreviewCard,
  VideoCallPreview
};